// Đơn hàng gần đây — cuối Tổng quan Bán hàng (31/10/2026): 5 đơn mới nhất của chính đơn vị (GET /orders?scope=self,
// như trang Đơn hàng ở chế độ Bán hàng), không theo khoảng đang chọn. Mỗi dòng mở chi tiết đơn. Điện thoại: tên khách /
// mã đơn · giờ bên trái, tiền thu hộ / trạng thái bên phải; máy tính: tiền / trạng thái thẳng cột, màn rộng (≥ 1280px)
// tách thêm cột mã đơn, giờ tạo và dòng số điện thoại khách.

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { get } from '@/lib/api'
import { cn } from '@/lib/utils'
import { money, phoneDisplay, shortOrderId } from '@/lib/format'
import { useViewportMode } from '@/hooks/useViewportMode'
import { Skeleton } from '@/components/ui/skeleton'
import { IndexBadge } from '@/components/ui/data-table'
import { LoadError } from '@/components/ui/data-notice'
import { StatusPill } from '@/components/ui/status'
import { vnDateKey } from '@shoptrack/common/date-range'

const LIMIT = 5

interface RecentOrder {
  id: string
  cod: number
  status: string
  createdAt: string
  recipientName?: string | null
  customer?: { name: string | null; phone: string | null } | null
}

const timeFmt = new Intl.DateTimeFormat('vi-VN', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Asia/Ho_Chi_Minh' })

/** "14:32" hôm nay, "29/10 14:32" ngày khác, "29/10/2025 14:32" năm khác (giờ VN). Dùng chung với RecentUnits. */
export function recentWhen(iso: string): string {
  const d = new Date(iso)
  const t = timeFmt.format(d)
  const key = vnDateKey(d)
  const today = vnDateKey(new Date())
  if (key === today) return t
  const year = key.slice(0, 4) === today.slice(0, 4) ? '' : `/${key.slice(0, 4)}`
  return `${key.slice(8, 10)}/${key.slice(5, 7)}${year} ${t}`
}

const nameOf = (o: RecentOrder) => o.customer?.name?.trim() || o.recipientName?.trim() || 'Khách lẻ'

export function RecentOrders() {
  const phone = useViewportMode() === 'mobile'
  const q = useQuery({
    queryKey: ['orders', 'recent', LIMIT],
    queryFn: () => get<{ orders: RecentOrder[] }>(`/orders?scope=self&limit=${LIMIT}`),
    refetchInterval: 30_000,
  })
  const orders = q.data?.orders ?? []

  return (
    <section className="min-w-0 overflow-hidden rounded-2xl bg-raise shadow-card dark:border dark:border-rule dark:shadow-none">
      <div className="flex items-center justify-between gap-3 px-5 pb-1 pt-4 desktop:px-6">
        <h2>Đơn hàng gần đây</h2>
        <Link
          to="/orders"
          className="motion-state -my-2.5 inline-flex min-h-11 shrink-0 items-center gap-0.5 text-[13px] font-semibold text-accent hover:underline desktop:my-0 desktop:min-h-8"
        >
          Xem tất cả
          <ChevronRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      {q.isError ? (
        <LoadError onRetry={() => q.refetch()} />
      ) : q.isLoading ? (
        <div className="space-y-3 px-5 pb-5 pt-3 desktop:px-6">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-11 w-full rounded-lg" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <p className="px-5 pb-5 pt-2 text-sm text-muted desktop:px-6">Chưa có đơn nào</p>
      ) : (
        <ul className="pb-2">
          {orders.map((o, i) => {
            const name = nameOf(o)
            return (
              <li key={o.id} className="border-t border-rule first:border-t-0">
                <Link
                  to={`/orders/${o.id}`}
                  className={cn(
                    'motion-state items-center gap-3 px-5 py-3 hover:bg-paperSoft/60 desktop:px-6',
                    phone ? 'flex' : 'grid grid-cols-[minmax(0,1fr)_8.5rem_8rem] gap-5 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_7rem_8.5rem_8rem]',
                  )}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent/10 text-sm font-semibold text-accent" aria-hidden>
                      {name.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <IndexBadge n={i + 1} />
                        <span className="truncate text-sm font-semibold text-ink">{name}</span>
                      </span>
                      <span className={cn('block truncate text-xs text-muted', !phone && 'xl:hidden')} title={o.id}>
                        <span className="font-mono">{shortOrderId(o.id)}</span> · {recentWhen(o.createdAt)}
                      </span>
                      {!phone && o.customer?.phone && (
                        <span className="hidden truncate text-xs tabular-nums text-muted xl:block">{phoneDisplay(o.customer.phone)}</span>
                      )}
                    </span>
                  </span>
                  {phone ? (
                    <span className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-sm font-semibold tabular-nums text-ink">{money(o.cod)}</span>
                      <StatusPill status={o.status} />
                    </span>
                  ) : (
                    <>
                      <span className="hidden truncate font-mono text-xs text-inkSoft xl:block" title={o.id}>
                        {shortOrderId(o.id)}
                      </span>
                      <span className="hidden text-right text-xs tabular-nums text-muted xl:block">{recentWhen(o.createdAt)}</span>
                      <span className="text-right text-sm font-semibold tabular-nums text-ink">{money(o.cod)}</span>
                      <span className="flex justify-end">
                        <StatusPill status={o.status} />
                      </span>
                    </>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
